import React, { lazy, Suspense, useCallback, useMemo } from 'react';

import { FileText, NotebookPen, Pencil } from 'lucide-react';

import OrderAttachmentSplitView from '@/features/admin/lab-order/components/attachement/order-attachment-split-view.component';
import LabResults from '@/features/admin/lab-order/components/results/lab-result.component';
import { useGetLabOrder } from '@/features/admin/lab-order/data-provider';
import { formatFullFillerStatus, getStatusVariant } from '@/features/admin/lab-order/utils';
import { EMPTY_RESULT } from '@/shared/constants';
import { LAB_ORDER_STATUS } from '@/shared/constants/order';
import type { Encounter, FulfillerStatus, MinimalOrder, Order } from '@/shared/types';
import { Badge, Button, Sheet, SheetContent, SheetHeader, SheetTitle, TooltipWrapper } from '@indi/ui';
import { cn } from '@indi/utils';

const LabResultForm = lazy(() => import('@/features/admin/lab-order/components/results/lab-result-form.component'));
const LabResultWithoutOrderForm = lazy(
  () => import('@/features/admin/lab-order/components/results/lab-result-without-order-form.component'),
);

export type PatientOrderResultsSheetMode = 'view' | 'write';

interface PatientOrderResultsSheetProps {
  order: MinimalOrder | null;
  open: boolean;
  mode?: PatientOrderResultsSheetMode;
  withoutOrder?: boolean;
  conceptUuid?: string;
  encounter?: Encounter | null;
  displayTitle?: string;
  patientUuid?: string;
  onClose: () => void;
  onModeChange?: (mode: PatientOrderResultsSheetMode) => void;
  onSettled: () => void;
  onSaved?: () => void;
  isRadiology?: boolean;
}

const PatientOrderResultsSheet = ({
  order,
  open,
  mode = 'view',
  withoutOrder = false,
  conceptUuid,
  encounter,
  displayTitle,
  patientUuid,
  onClose,
  onModeChange,
  onSettled,
  onSaved,
  isRadiology = false,
}: PatientOrderResultsSheetProps) => {
  const isWriteMode = mode === 'write';
  const hasLinkedOrder = Boolean(order && order.orderNumber !== 'N/A');
  const { data: liveOrder } = useGetLabOrder(open && hasLinkedOrder && order?.uuid ? order.uuid : '');

  const fulfillerStatus = useMemo<FulfillerStatus | null | undefined>(() => {
    if (hasLinkedOrder && liveOrder) return liveOrder.fulfillerStatus;
    return order?.fulfillerStatus;
  }, [hasLinkedOrder, liveOrder, order?.fulfillerStatus]);

  const sheetTitle = order?.display ?? displayTitle ?? (isRadiology ? 'Radiology report' : 'Lab report');
  const reportLabel = isRadiology ? 'Radiology report' : 'Lab report';
  const writeLabel = isRadiology ? 'Enter radiology results' : 'Enter lab results';
  const directWriteLabel = isRadiology ? 'Enter direct imaging result' : 'Enter direct lab result';
  const editLabel = isRadiology ? 'Edit radiology results' : 'Edit lab results';
  const isEditingCompleted = fulfillerStatus === LAB_ORDER_STATUS.COMPLETED;
  const headerEyebrow = isWriteMode
    ? isEditingCompleted
      ? editLabel
      : withoutOrder
        ? directWriteLabel
        : writeLabel
    : reportLabel;

  const canShowEdit = !isWriteMode && isEditingCompleted;
  const canShowWriteForm =
    isWriteMode && Boolean(patientUuid) && (hasLinkedOrder || (withoutOrder && Boolean(conceptUuid)));
  const canShowViewContent = !isWriteMode && order;

  const handleFormClose = useCallback(() => {
    if (isWriteMode && isEditingCompleted) {
      onModeChange?.('view');
      return;
    }
    onClose();
  }, [isWriteMode, isEditingCompleted, onClose, onModeChange]);

  const handleFormSaved = useCallback(() => {
    onSettled();
    if (isWriteMode) {
      // ponytail: keep sheet open after save; only close for brand-new direct results (no linked order yet)
      if (withoutOrder && !order) {
        onSaved?.();
        return;
      }
      onModeChange?.('view');
      return;
    }
    onSaved?.();
  }, [isWriteMode, withoutOrder, order, onModeChange, onSaved, onSettled]);

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className={cn(
          'flex h-full w-full min-w-0 max-w-full flex-col gap-0 overflow-hidden bg-gray-50 p-0 sm:max-w-none',
          'md:w-[75vw] md:min-w-[960px] md:max-w-[min(95vw,1400px)]',
        )}
      >
        <style>
          {`
            body [class*="_modalContainer_"] {
              pointer-events: auto;
            }
          `}
        </style>
        <SheetHeader className="shrink-0 border-b border-gray-200 bg-white/95 px-4 py-4 pr-24 shadow-sm sm:px-6 sm:pr-28">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3 text-left">
              <div className="mt-0.5 rounded-xl bg-primary-light p-2 text-primary-dark">
                {isWriteMode ? <NotebookPen className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">{headerEyebrow}</p>
                <SheetTitle className="truncate text-left text-xl font-semibold text-gray-950">{sheetTitle}</SheetTitle>
                {order || isEditingCompleted ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Status</span>
                    <Badge
                      variant={getStatusVariant(fulfillerStatus)}
                      className="h-5 px-2 text-[10px] font-semibold uppercase tracking-wider"
                    >
                      {formatFullFillerStatus(fulfillerStatus) || EMPTY_RESULT}
                    </Badge>
                  </div>
                ) : null}
              </div>
            </div>
            {canShowEdit ? (
              <TooltipWrapper content={editLabel}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={editLabel}
                  onClick={() => onModeChange?.('write')}
                  className="shrink-0 gap-2 border-gray-200 bg-white shadow-sm"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              </TooltipWrapper>
            ) : null}
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-5 sm:px-6">
          {canShowWriteForm ? (
            <Suspense>
              {withoutOrder ? (
                <LabResultWithoutOrderForm
                  key={`write-without-${conceptUuid}-${order?.uuid ?? 'new'}`}
                  conceptUuid={conceptUuid!}
                  patientUuid={patientUuid!}
                  isRadiology={isRadiology}
                  layout="sheet"
                  encounter={encounter ?? undefined}
                  order={order as Order | undefined}
                  onClose={handleFormClose}
                  onSaved={handleFormSaved}
                />
              ) : (
                <LabResultForm
                  key={`write-${order!.uuid}`}
                  orderUuid={order!.uuid}
                  isRadiology={isRadiology}
                  layout="sheet"
                  onClose={handleFormClose}
                  onSaved={handleFormSaved}
                />
              )}
            </Suspense>
          ) : null}

          {canShowViewContent ? (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <LabResults
                order={(liveOrder ?? order) as Order}
                isLabOrderMetaDataVisible
                isRadiologyView={isRadiology}
                isSheetView
                hideAttachments
                onSettled={onSettled}
              />
              <div className="mt-4">
                <OrderAttachmentSplitView
                  testOrder={(liveOrder ?? order) as Order}
                  isRadiology={isRadiology}
                  onSettled={onSettled}
                />
              </div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PatientOrderResultsSheet;
